// PATH: src/components/modules/devtools/panels/ChangesPanel.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import ChangesPreviewDiffDialog from "../components/ChangesPreviewDiffDialog";
import { downloadWithAuth } from "../utils/devtoolsAuth";

/**
 * ChangesPanel
 *
 * A lightweight view showing:
 * - Left: list of all changes
 * - Right: for selected change → file list + change history
 * - Editable description + status for selected change
 */

// EXTENDED STATUS PIPELINE
const STATUS_OPTIONS = [
  "OPEN",
  "IN_PROGRESS",
  "DEVELOPMENT",
  "SANDBOX_TESTED",
  "INSTALLED_TO_TEST",
  "TESTED",
  "INSTALLED_TO_PROD",
  "DONE",
  "CANCELLED",
];

// Fixed developer options for now
const DEVELOPER_OPTIONS = ["UNASSIGNED", "Chris", "Nathan"];

const statusColor = (status) => {
  switch (status) {
    case "OPEN":
      return "default";
    case "IN_PROGRESS":
    case "DEVELOPMENT":
      return "primary";
    case "SANDBOX_TESTED":
      return "secondary";
    case "INSTALLED_TO_TEST":
      return "warning";
    case "TESTED":
      return "info";
    case "INSTALLED_TO_PROD":
    case "DONE":
      return "success";
    case "CANCELLED":
      return "warning";
    default:
      return "default";
  }
};

const ChangesPanel = ({
  selectedProjectId: sharedSelectedProjectId,
  onSelectedProjectChange,
}) => {
  const [changes, setChanges] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesError, setChangesError] = useState(null);

  const [selectedChange, setSelectedChange] = useState(null);

  const [changeFiles, setChangeFiles] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  // NEW: filter by status for changes list
  const [statusFilter, setStatusFilter] = useState("ALL");

  // NEW: filter by developer for changes list
  const [developerFilter, setDeveloperFilter] = useState("ALL");

  const [historyFilter, setHistoryFilter] = useState("ALL");

  // NEW: editable fields + save state
  const [editDescription, setEditDescription] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editDeveloper, setEditDeveloper] = useState("UNASSIGNED");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [saveMetaLoading, setSaveMetaLoading] = useState(false);
  const [saveMetaError, setSaveMetaError] = useState(null);
  const [saveMetaMessage, setSaveMetaMessage] = useState(null);
  const [revertWholeChangeLoading, setRevertWholeChangeLoading] = useState(false);

  // NEW: preview diff dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Change package import/export
  const fileInputRef = useRef(null);
  const [importMode, setImportMode] = useState("APPLY_FILES"); // APPLY_FILES | STAGE_ONLY | PLANNED_ONLY
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgMessage, setPkgMessage] = useState(null);
  const [pkgError, setPkgError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [localSourceProjectId, setLocalSourceProjectId] = useState("");
  const sourceProjectId =
    typeof sharedSelectedProjectId === "string"
      ? sharedSelectedProjectId
      : localSourceProjectId;
  const setSourceProjectId =
    typeof onSelectedProjectChange === "function"
      ? onSelectedProjectChange
      : setLocalSourceProjectId;
  const [targetProjectId, setTargetProjectId] = useState("");
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateError, setMigrateError] = useState(null);
  const [migrateMessage, setMigrateMessage] = useState(null);

  const pickDefaultSourceProjectId = (projectList) => {
    const list = projectList || [];
    const sandbox = list.find((p) =>
      /sandbox/i.test(`${p?.project_id || ""} ${p?.label || ""}`)
    );
    return sandbox?.project_id || list[0]?.project_id || "";
  };

  const pickDefaultTargetProjectId = (projectList, currentSourceProjectId) => {
    const list = (projectList || []).filter(
      (p) => p && p.project_id && p.project_id !== currentSourceProjectId
    );
    const testProject = list.find((p) =>
      /test/i.test(
        `${p?.project_id || ""} ${p?.label || ""} ${p?.project_notes || ""}`
      )
    );
    return testProject?.project_id || list[0]?.project_id || "";
  };

  useEffect(() => {
    const loadProjects = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const res = await fetch("/api/dev-ide/projects");
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || "Failed to load projects.");
        }

        const list = data.projects || [];
        setProjects(list);

        const nextSourceProjectId =
          sourceProjectId || pickDefaultSourceProjectId(list);
        if (!sourceProjectId && nextSourceProjectId) {
          setSourceProjectId(nextSourceProjectId);
        }

        if (!targetProjectId) {
          const nextTargetProjectId = pickDefaultTargetProjectId(
            list,
            nextSourceProjectId
          );
          if (nextTargetProjectId) {
            setTargetProjectId(nextTargetProjectId);
          }
        }
      } catch (err) {
        setProjectsError(err.message);
      } finally {
        setProjectsLoading(false);
      }
    };

    loadProjects();
  }, [setSourceProjectId, sourceProjectId, targetProjectId]);

  useEffect(() => {
    if (!projects.length) return;
    if (!sourceProjectId) {
      const nextSourceProjectId = pickDefaultSourceProjectId(projects);
      if (nextSourceProjectId) {
        setSourceProjectId(nextSourceProjectId);
      }
      return;
    }

    if (!targetProjectId || targetProjectId === sourceProjectId) {
      const nextTargetProjectId = pickDefaultTargetProjectId(
        projects,
        sourceProjectId
      );
      if (nextTargetProjectId && nextTargetProjectId !== targetProjectId) {
        setTargetProjectId(nextTargetProjectId);
      }
    }
  }, [projects, setSourceProjectId, sourceProjectId, targetProjectId]);

  // ---------------------------------------------------------
  // LOAD LIST OF CHANGES
  // ---------------------------------------------------------
  useEffect(() => {
    const loadChanges = async () => {
      setChangesLoading(true);
      setChangesError(null);
      try {
        const res = await fetch("/api/dev-ide/changes");
        const data = await res.json();
        if (!data.success)
          throw new Error(data.error || "Failed to load changes.");
        setChanges(data.changes || []);
      } catch (err) {
        setChangesError(err.message);
      } finally {
        setChangesLoading(false);
      }
    };
    loadChanges();
  }, []);

  // ---------------------------------------------------------
  // LOAD DETAILS FOR SELECTED CHANGE
  // ---------------------------------------------------------
  const loadChangeDetails = useCallback(async (changeId) => {
    setDetailsLoading(true);
    setDetailsError(null);
    setChangeFiles([]);
    setChangeHistory([]);

    try {
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(changeId)}${
          sourceProjectId
            ? `?project_id=${encodeURIComponent(sourceProjectId)}`
            : ""
        }`
      );
      const data = await res.json();
      if (!data.success)
        throw new Error(data.error || "Failed to load change details.");

      setChangeFiles(data.files || []);
      setChangeHistory(data.history || []);
    } catch (err) {
      setDetailsError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  }, [sourceProjectId]);

  const handleSelectChange = (ch) => {
    setSelectedChange(ch);
    setSaveMetaError(null);
    setSaveMetaMessage(null);
    setMigrateError(null);
    setMigrateMessage(null);
    setDescriptionModalOpen(false);

    // Initialise editable fields from selected change
    setEditDescription(ch?.description || "");
    setEditStatus(ch?.status || "OPEN");

    // NEW: initialise developer from selected change (or UNASSIGNED)
    const dev = ch?.developer || ch?.assignee || null;
    setEditDeveloper(dev || "UNASSIGNED");

    if (ch && ch.id) {
      loadChangeDetails(ch.id);
    }
  };


  useEffect(() => {
    if (selectedChange && selectedChange.id && sourceProjectId) {
      loadChangeDetails(selectedChange.id);
    }
  }, [loadChangeDetails, selectedChange, sourceProjectId]);

  const parseDocSlug = (entry) => {
    if (!entry) return null;
    if (entry.doc_slug) return entry.doc_slug;
    const summary = String(entry.save_summary || "");
    const marker = "[doc_slug=";
    if (!summary.includes(marker)) return null;
    return summary.split(marker)[1]?.split("]")[0] || null;
  };

  const isDocumentationHistoryEntry = (entry) => {
    if (!entry) return false;
    if (entry.asset_type === "documentation") return true;
    return String(entry.save_summary || "").includes("[asset_type=documentation]");
  };

  const getBackupName = (bp) => {
    if (!bp) return '';
    const s = String(bp);
    const parts = s.split('/');
    return parts[parts.length - 1];
  };

  const handleRevertHistoryEntry = async (h) => {
    if (!h) return;

    if (isDocumentationHistoryEntry(h)) {
      const slug = parseDocSlug(h);
      const versionName = h.backup_path;
      if (!slug) {
        setSaveMetaMessage("Could not resolve document slug for this history entry.");
        return;
      }
      if (!versionName) {
        setSaveMetaMessage("No version available for this documentation history entry.");
        return;
      }

      const ok = window.confirm(`Revert documentation to this version?\n\n${versionName}`);
      if (!ok) return;

      try {
        const res = await fetch("/api/dev-ide/revert-imported-document", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slug,
            version_name: versionName,
            username: "dev-ide",
            change_id: selectedChange && selectedChange.id ? selectedChange.id : null,
            project_id: sourceProjectId || null,
            change_summary: `Reverted documentation: ${slug}`,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || "Failed to revert document.");

        setSaveMetaMessage(data.message || "Reverted document successfully.");
        if (selectedChange && selectedChange.id) {
          await loadChangeDetails(selectedChange.id);
        }
      } catch (err) {
        setSaveMetaMessage(`Error reverting document: ${err.message}`);
      }
      return;
    }
    const backupPath = h.backup_path;
    if (!backupPath) {
      setSaveMetaMessage('No backup path available for this history entry.');
      return;
    }

    const fileIdByPath = {};
    (changeFiles || []).forEach((f) => {
      const path = f.file_path || f.path;
      if (path && f.file_id) fileIdByPath[path] = f.file_id;
    });

    const filePath = h.file_path;
    const fileId = fileIdByPath[filePath];
    if (!fileId) {
      setSaveMetaMessage('Could not resolve file_id for this history entry.');
      return;
    }

    const backupName = getBackupName(backupPath);
    const ok = window.confirm(`Revert file to this backup?

${backupName}`);
    if (!ok) return;

    try {
      const res = await fetch('/api/dev-ide/revert-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: fileId,
          backup_path: backupPath,
          username: 'dev-ide',
          change_id: selectedChange && selectedChange.id ? selectedChange.id : null,
          project_id: sourceProjectId || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to revert file.');

      setSaveMetaMessage(data.message || 'Reverted file successfully.');
      if (selectedChange && selectedChange.id) {
        await loadChangeDetails(selectedChange.id);
      }
    } catch (err) {
      setSaveMetaMessage(`Error reverting file: ${err.message}`);
    }
  };



  const handleRevertWholeChange = async () => {
    if (!selectedChange || !selectedChange.id) return;

    const ok = window.confirm(
      `Revert all files for change #${selectedChange.id} back to their pre-change state?\n\nThis will delete files created in the change and restore earlier file contents.`
    );
    if (!ok) return;

    setRevertWholeChangeLoading(true);
    setSaveMetaError(null);
    setSaveMetaMessage(null);

    try {
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(selectedChange.id)}/revert`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: "dev-ide",
            project_id: sourceProjectId || null,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to revert change.");
      }

      const restoredCount = Number(data.restored_count || 0);
      const deletedCount = Number(data.deleted_count || 0);
      const updatedChange = data.updated_change || null;
      if (updatedChange && updatedChange.id) {
        setSelectedChange(updatedChange);
        setEditStatus(updatedChange.status || "");
        setChanges((prev) =>
          (prev || []).map((c) => (c.id === updatedChange.id ? updatedChange : c))
        );
      }
      setSaveMetaMessage(
        `Reverted change. Restored ${restoredCount} file(s), deleted ${deletedCount} file(s). Status set to CANCELLED.`
      );

      await loadChangeDetails(selectedChange.id);
    } catch (err) {
      setSaveMetaError(err.message);
    } finally {
      setRevertWholeChangeLoading(false);
    }
  };

  // ---------------------------------------------------------
  // SAVE DESCRIPTION + STATUS + DEVELOPER
  // ---------------------------------------------------------
  const handleSaveChangeMeta = async () => {
    if (!selectedChange || !selectedChange.id) return;

    setSaveMetaLoading(true);
    setSaveMetaError(null);
    setSaveMetaMessage(null);

    const developerPayload =
      editDeveloper === "UNASSIGNED" ? null : editDeveloper;

    try {
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(
          selectedChange.id
        )}/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            description: editDescription,
            status: editStatus,
            developer: developerPayload,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to update change.");
      }

      const updated =
        data.change || {
          ...selectedChange,
          description: editDescription,
          status: editStatus,
          developer: developerPayload,
        };

      // Update selected change
      setSelectedChange(updated);

      // Update list
      setChanges((prev) =>
        (prev || []).map((c) => (c.id === updated.id ? updated : c))
      );

      setSaveMetaMessage("Change metadata saved.");
    } catch (err) {
      setSaveMetaError(err.message);
    } finally {
      setSaveMetaLoading(false);
    }
  };

// PREVIEW DIFF HANDLER
  // ---------------------------------------------------------
  const handlePreviewDiff = async (historyItem) => {
    const logId = historyItem && historyItem.preview_log_id;
    if (!logId) return;

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const res = await fetch(
        `/api/dev-ide/preview-diff-log?log_id=${encodeURIComponent(logId)}`
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to load preview diff.");
      }
      setPreviewData(data.log || null);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewError(null);
    setPreviewData(null);
  };

  const filteredChangeHistory = (changeHistory || []).filter((entry) => {
    if (historyFilter === "DOCUMENTATION") {
      return isDocumentationHistoryEntry(entry);
    }
    if (historyFilter === "CODE") {
      return !isDocumentationHistoryEntry(entry);
    }
    return true;
  });

  const handleExportPackage = () => {
    if (!selectedChange || !selectedChange.id) return;
    setPkgError(null);
    setPkgMessage(null);
    const url = `/api/dev-ide/changes/${encodeURIComponent(
      selectedChange.id
    )}/export-package${
      sourceProjectId
        ? `?project_id=${encodeURIComponent(sourceProjectId)}`
        : ""
    }`;
    downloadWithAuth(url).catch((err) => {
      setPkgError(err.message || "Failed to export package.");
    });
  };

  const handleExportPackageForChatGPT = () => {
    if (!selectedChange || !selectedChange.id) return;
    setPkgError(null);
    setPkgMessage(null);
    // Download in browser (includes module context for planning)
    const params = new URLSearchParams();
    params.set("include_module", "1");
    params.set("export_kind", "chat_planning");
    if (sourceProjectId) {
      params.set("project_id", sourceProjectId);
    }
    const url = `/api/dev-ide/changes/${encodeURIComponent(
      selectedChange.id
    )}/export-package?${params.toString()}`;
    downloadWithAuth(url).catch((err) => {
      setPkgError(err.message || "Failed to export package.");
    });
  };


  const handleClickImportPackage = (mode) => {
    setPkgError(null);
    setPkgMessage(null);
    setImportMode(mode || "APPLY_FILES");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleImportPackageSelected = async (ev) => {
    const file = ev && ev.target && ev.target.files ? ev.target.files[0] : null;
    if (!file) return;

    const isPlanned = importMode === "PLANNED_ONLY";
    const isStage = importMode === "STAGE_ONLY";

    const ok = window.confirm(
      isPlanned
        ? "Import this planned change package now?\n\nThis will UPDATE the change details only (no file changes will be applied)."
        : isStage
        ? "Import this change package in STAGE mode now?\n\nThis will store diffs/content for review and later apply (no disk writes yet)."
        : "Import this change package now?\n\nThis will APPLY the files and create backups."
    );
    if (!ok) return;

    setPkgLoading(true);
    setPkgError(null);
    setPkgMessage(null);

    try {
      const form = new FormData();
      form.append("zip_file", file);
      if (sourceProjectId) {
        form.append("project_id", sourceProjectId);
      }
      // Backend supports mode: plan_only | stage | apply
      if (importMode === "PLANNED_ONLY") {
        form.append("mode", "plan_only");
        form.append("apply", "0");
      } else if (importMode === "STAGE_ONLY") {
        form.append("mode", "stage");
        form.append("apply", "0");
      } else {
        form.append("mode", "apply");
        form.append("apply", "1");
      }
      // If a change is selected, import into it (update). Otherwise create new.
      if (selectedChange && selectedChange.id) {
        form.append("target_change_id", String(selectedChange.id));
        form.append("create_new", "0");
      } else {
        if (importMode === "PLANNED_ONLY") {
          throw new Error("Select a change first before importing a planned change package.");
        }
        form.append("create_new", "1");
      }

      const res = await fetch("/api/dev-ide/changes/import-package", {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to import change package.");
      }

      const importedChange = data.change || null;
      const appliedCount = (data.applied || []).length;
      const errorCount = (data.errors || []).length;

      const actionLabel =
        importMode === "PLANNED_ONLY"
          ? "Updated change details."
          : importMode === "STAGE_ONLY"
          ? `Staged ${appliedCount} file(s).`
          : `Applied ${appliedCount} file(s).`;

      setPkgMessage(
        `Imported package. ${actionLabel}${errorCount ? ` Errors: ${errorCount}.` : ""}`
      );

      // Refresh changes list + details
      try {
        const listRes = await fetch("/api/dev-ide/changes");
        const listData = await listRes.json();
        if (listData && listData.success) {
          const newList = listData.changes || [];
          setChanges(newList);

          if (importedChange && importedChange.id) {
            const found = newList.find((c) => c && c.id === importedChange.id);
            if (found) {
              handleSelectChange(found);
            } else {
              setSelectedChange(importedChange);
              loadChangeDetails(importedChange.id);
            }
          } else if (selectedChange && selectedChange.id) {
            await loadChangeDetails(selectedChange.id);
          }
        }
      } catch (e) {
        if (selectedChange && selectedChange.id) {
          await loadChangeDetails(selectedChange.id);
        }
      }
    } catch (err) {
      setPkgError(err.message);
    } finally {
      setPkgLoading(false);
    }
  };

  const handleRunMigration = async (mode) => {
    if (!selectedChange || !selectedChange.id) return;

    if (!sourceProjectId) {
      setMigrateError("Select a source project first.");
      return;
    }
    if (!targetProjectId) {
      setMigrateError("Select a target project first.");
      return;
    }
    if (sourceProjectId === targetProjectId) {
      setMigrateError("Source and target projects must be different.");
      return;
    }

    const isPreview = mode === "preview";
    const ok = window.confirm(
      isPreview
        ? `Preview migrating files for change #${selectedChange.id} from ${sourceProjectId} to ${targetProjectId}?`
        : `Install files for change #${selectedChange.id} from ${sourceProjectId} to ${targetProjectId} now?\n\nThis will write files into the target project and create backups.`
    );
    if (!ok) return;

    setMigrateLoading(true);
    setMigrateError(null);
    setMigrateMessage(null);

    try {
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(selectedChange.id)}/migrate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source_project_id: sourceProjectId,
            target_project_id: targetProjectId,
            mode: isPreview ? "preview" : "apply",
            include_sql: true,
            run_scans: !isPreview,
            rebuild_frontend: false,
            restart_backend: false,
            username: "dev-ide",
            change_summary: `migrate change to ${targetProjectId}`,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to migrate change files.");
      }

      const summary = data.summary || {};
      const changedCount = Number(summary.changed_count || 0);
      const appliedCount = Array.isArray(data.applied) ? data.applied.length : 0;
      const errorCount = Array.isArray(data.errors) ? data.errors.length : 0;

      setMigrateMessage(
        isPreview
          ? `Preview ready for ${targetProjectId}. ${changedCount} file(s) differ.${
              errorCount ? ` Errors: ${errorCount}.` : ""
            }`
          : `Installed ${appliedCount} file(s) to ${targetProjectId}.${
              errorCount ? ` Errors: ${errorCount}.` : ""
            }`
      );

      if (data.change && data.change.id) {
        const updatedChange = data.change;
        setSelectedChange(updatedChange);
        setChanges((prev) =>
          (prev || []).map((c) => (c.id === updatedChange.id ? updatedChange : c))
        );
      }

      if (!isPreview && selectedChange && selectedChange.id) {
        await loadChangeDetails(selectedChange.id);
      }
    } catch (err) {
      setMigrateError(err.message);
    } finally {
      setMigrateLoading(false);
    }
  };

  // ---------------------------------------------------------
  // DERIVED: FILTERED CHANGES LIST (LEFT PANE)
  // ---------------------------------------------------------
  const filteredChanges = (changes || []).filter((ch) => {
    // Status filter
    if (statusFilter && statusFilter !== "ALL" && ch.status !== statusFilter) {
      return false;
    }

    // Developer filter
    if (developerFilter && developerFilter !== "ALL") {
      if (developerFilter === "UNASSIGNED") {
        return !ch.developer && !ch.assignee;
      }
      return ch.developer === developerFilter || ch.assignee === developerFilter;
    }

    return true;
  });

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  const handleApplyStagedFiles = async () => {
    if (!selectedChange || !selectedChange.id) return;

    const ok = window.confirm(
      "Apply all STAGED files for this change now?\n\nThis will write files to disk and create backups."
    );
    if (!ok) return;

    setPkgLoading(true);
    setPkgError(null);
    setPkgMessage(null);

    try {
      const form = new FormData();
      form.append("change_summary", "apply staged files");
      if (sourceProjectId) {
        form.append("project_id", sourceProjectId);
      }
      const res = await fetch(
        `/api/dev-ide/changes/${encodeURIComponent(selectedChange.id)}/apply-staged`,
        {
          method: "POST",
          body: form,
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to apply staged files.");

      const appliedCount = (data.applied || []).length;
      const errorCount = (data.errors || []).length;

      setPkgMessage(
        `Applied staged files. Applied ${appliedCount} file(s).${
          errorCount ? ` Errors: ${errorCount}.` : ""
        }`
      );

      if (selectedChange && selectedChange.id) {
        await loadChangeDetails(selectedChange.id);
      }
    } catch (err) {
      setPkgError(err.message);
    } finally {
      setPkgLoading(false);
    }
  };


  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: "none" }}
        onChange={handleImportPackageSelected}
      />
      <Box sx={{ display: "flex", gap: 2, height: "100%", minHeight: "600px" }}>
        {/* LEFT: Change List */}
        <Paper
          elevation={2}
          sx={{
            width: "28%",
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
          }}
        >
          <Box p={2} borderBottom="1px solid rgba(0,0,0,0.12)">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <div>
                <Typography variant="h6">Changes</Typography>
                <Typography variant="caption" color="text.secondary">
                  View &amp; edit changes, inspect files &amp; history
                </Typography>
              </div>
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  try {
                    const requestedTitle = window.prompt("Enter a title for the new change:");
                    if (!requestedTitle || !requestedTitle.trim()) {
                      return;
                    }

                    const payload = { title: requestedTitle.trim() };
                    if (developerFilter && developerFilter !== "ALL" && developerFilter !== "UNASSIGNED") {
                      payload.developer = developerFilter;
                    }
                    const res = await fetch("/api/dev-ide/changes", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                    });
                    const data = await res.json();
                    if (!data.success) {
                      throw new Error(data.error || "Failed to create change.");
                    }
                    const created = data.change;
                    try {
                      const listRes = await fetch("/api/dev-ide/changes");
                      const listData = await listRes.json();
                      if (listData && listData.success) {
                        const newList = listData.changes || [];
                        setChanges(newList);
                        const found = newList.find((c) => c && c.id === (created && created.id));
                        if (found) {
                          handleSelectChange(found);
                        } else if (created && created.id) {
                          setSelectedChange(created);
                          loadChangeDetails(created.id);
                        }
                      } else {
                        if (created) {
                          setChanges((prev) => [created, ...(prev || [])]);
                          setSelectedChange(created);
                          if (created.id) loadChangeDetails(created.id);
                        }
                      }
                    } catch (e) {
                      if (created) {
                        setChanges((prev) => [created, ...(prev || [])]);
                        setSelectedChange(created);
                        if (created.id) loadChangeDetails(created.id);
                      }
                    }
                  } catch (err) {
                    console.error(err);
                    alert(err.message || "Failed to create change.");
                  }
                }}
              >
                New Change
              </Button>
            </Stack>
          </Box>

          {/* Filters for changes list */}
          <Box
            sx={{
              px: 2,
              pt: 1,
              pb: 1,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Stack direction="column" spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel id="status-filter-label">Filter by status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Filter by status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All statuses</MenuItem>
                  {STATUS_OPTIONS.map((st) => (
                    <MenuItem key={st} value={st}>
                      {st}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="developer-filter-label">
                  Filter by developer
                </InputLabel>
                <Select
                  labelId="developer-filter-label"
                  label="Filter by developer"
                  value={developerFilter}
                  onChange={(e) => setDeveloperFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All developers</MenuItem>
                  {DEVELOPER_OPTIONS.map((dev) => (
                    <MenuItem key={dev} value={dev}>
                      {dev === "UNASSIGNED" ? "Unassigned" : dev}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {changesLoading && (
              <Box p={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            )}

            {changesError && (
              <Typography color="error" p={2}>
                {changesError}
              </Typography>
            )}

            <List dense>
              {filteredChanges.map((ch) => {
                const devLabel =
                  ch.developer || ch.assignee || "Unassigned";

                return (
                  <ListItemButton
                    key={ch.id}
                    selected={selectedChange && selectedChange.id === ch.id}
                    onClick={() => handleSelectChange(ch)}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>{`#${ch.id} ${ch.title}`}</span>
                          {ch.status && (
                            <Chip
                              label={ch.status}
                              size="small"
                              color={statusColor(ch.status)}
                              variant="outlined"
                            />
                          )}
                          {devLabel && (
                            <Chip
                              label={devLabel}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          {ch.module && (
                            <span>
                              Module: <strong>{ch.module}</strong>
                            </span>
                          )}
                          {ch.description && (
                            <div
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {ch.description}
                            </div>
                          )}
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </Paper>

        {/* RIGHT: Details */}
        <Paper
          elevation={2}
          sx={{ flex: 1, display: "flex", flexDirection: "column", maxHeight: "100%" }}
        >
          <Box p={2} borderBottom="1px solid rgba(0,0,0,0.12)">
            {selectedChange ? (
              <>
                <Typography variant="h6">
                  Change #{selectedChange.id}: {selectedChange.title}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mt: 0.5, flexWrap: "wrap" }}
                >
                  {selectedChange.module && (
                    <Typography variant="caption" color="text.secondary">
                      Module: <strong>{selectedChange.module}</strong>
                    </Typography>
                  )}
                  {selectedChange.status && (
                    <Chip
                      label={selectedChange.status}
                      size="small"
                      color={statusColor(selectedChange.status)}
                      variant="filled"
                    />
                  )}
                  {/* NEW: show developer in header */}
                  <Typography variant="caption" color="text.secondary">
                    Developer:{" "}
                    <strong>
                      {selectedChange.developer ||
                        selectedChange.assignee ||
                        "Unassigned"}
                    </strong>
                  </Typography>
                </Stack>
              </>
            ) : (
              <Typography variant="h6">Select a change to view details</Typography>
            )}
          </Box>

          {!selectedChange && (
            <Box p={3} color="text.secondary">
              Choose a change from the left sidebar.
            </Box>
          )}

          {selectedChange && (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Editable meta */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0,0,0,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="change-status-label">Status</InputLabel>
                    <Select
                      labelId="change-status-label"
                      label="Status"
                      value={editStatus || "OPEN"}
                      onChange={(e) => setEditStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((st) => (
                        <MenuItem key={st} value={st}>
                          {st}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* NEW: Developer selector */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="change-developer-label">
                      Developer
                    </InputLabel>
                    <Select
                      labelId="change-developer-label"
                      label="Developer"
                      value={editDeveloper}
                      onChange={(e) => setEditDeveloper(e.target.value)}
                    >
                      {DEVELOPER_OPTIONS.map((dev) => (
                        <MenuItem key={dev} value={dev}>
                          {dev === "UNASSIGNED" ? "Unassigned" : dev}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={10}
                      size="small"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      onClick={() => setDescriptionModalOpen(true)}
                      inputProps={{ "aria-label": "Change description" }}
                      sx={{
                        "& .MuiInputBase-root": {
                          alignItems: "flex-start",
                          cursor: "text",
                        },
                        "& textarea": {
                          cursor: "text",
                        },
                      }}
                    />
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="change-source-project-label">
                      Source project
                    </InputLabel>
                    <Select
                      labelId="change-source-project-label"
                      label="Source project"
                      value={sourceProjectId || ""}
                      onChange={(e) => setSourceProjectId(e.target.value)}
                      disabled={projectsLoading || !projects.length}
                    >
                      {(projects || []).map((project) => (
                        <MenuItem
                          key={project.project_id}
                          value={project.project_id}
                        >
                          {project.label || project.project_id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="change-target-project-label">
                      Target project
                    </InputLabel>
                    <Select
                      labelId="change-target-project-label"
                      label="Target project"
                      value={targetProjectId || ""}
                      onChange={(e) => setTargetProjectId(e.target.value)}
                      disabled={projectsLoading || !projects.length}
                    >
                      {(projects || [])
                        .filter((project) => project.project_id !== sourceProjectId)
                        .map((project) => (
                          <MenuItem
                            key={project.project_id}
                            value={project.project_id}
                          >
                            {project.label || project.project_id}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Typography variant="caption" color="text.secondary">
                    {projectsLoading
                      ? "Loading projects..."
                      : sourceProjectId && targetProjectId
                      ? `Promote from ${sourceProjectId} to ${targetProjectId}`
                      : "Select source and target projects."}
                  </Typography>
                </Stack>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveChangeMeta}
                    disabled={saveMetaLoading}
                  >
                    {saveMetaLoading ? "Saving..." : "Save change details"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={handleRevertWholeChange}
                    disabled={!selectedChange || !selectedChange.id || revertWholeChangeLoading}
                  >
                    {revertWholeChangeLoading ? "Reverting..." : "Revert Change"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportPackage}
                    disabled={!selectedChange || !selectedChange.id || pkgLoading}
                  >
                    Export Package
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportPackageForChatGPT}
                    disabled={!selectedChange || !selectedChange.id || pkgLoading}
                  >
                    Export for ChatGPT
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleClickImportPackage("APPLY_FILES")}
                    disabled={pkgLoading}
                  >
                    {pkgLoading ? "Importing..." : "Import Package"}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleClickImportPackage("STAGE_ONLY")}
                    disabled={!selectedChange || !selectedChange.id || pkgLoading}
                  >
                    Import (Stage)
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyStagedFiles}
                    disabled={!selectedChange || !selectedChange.id || pkgLoading}
                  >
                    Apply Staged
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRunMigration("preview")}
                    disabled={
                      !selectedChange ||
                      !selectedChange.id ||
                      migrateLoading ||
                      !sourceProjectId ||
                      !targetProjectId
                    }
                  >
                    {migrateLoading ? "Working..." : "Preview to Target"}
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleRunMigration("apply")}
                    disabled={
                      !selectedChange ||
                      !selectedChange.id ||
                      migrateLoading ||
                      !sourceProjectId ||
                      !targetProjectId
                    }
                  >
                    Install to Target
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleClickImportPackage("PLANNED_ONLY")}
                    disabled={!selectedChange || !selectedChange.id || pkgLoading}
                  >
                    Import Planned
                  </Button>
                  {saveMetaError && (
                    <Typography variant="caption" color="error">
                      {saveMetaError}
                    </Typography>
                  )}
                  {saveMetaMessage && (
                    <Typography variant="caption" sx={{ color: "success.main" }}>
                      {saveMetaMessage}
                    </Typography>
                  )}
                  {pkgError && (
                    <Typography variant="caption" color="error">
                      {pkgError}
                    </Typography>
                  )}
                  {pkgMessage && (
                    <Typography variant="caption" sx={{ color: "success.main" }}>
                      {pkgMessage}
                    </Typography>
                  )}
                  {projectsError && (
                    <Typography variant="caption" color="error">
                      {projectsError}
                    </Typography>
                  )}
                  {migrateError && (
                    <Typography variant="caption" color="error">
                      {migrateError}
                    </Typography>
                  )}
                  {migrateMessage && (
                    <Typography variant="caption" sx={{ color: "success.main" }}>
                      {migrateMessage}
                    </Typography>
                  )}
                </Box>
              </Box>

              {detailsLoading && (
                <Box p={2} display="flex" justifyContent="center">
                  <CircularProgress size={24} />
                </Box>
              )}

              {detailsError && (
                <Typography color="error" p={2}>
                  {detailsError}
                </Typography>
              )}

              {!detailsLoading && !detailsError && (
                <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                  {/* Files */}
                  <Box
                    sx={{
                      width: "50%",
                      borderRight: "1px solid rgba(0,0,0,0.12)",
                      overflowY: "auto",
                    }}
                  >
                    <Box p={2}>
                      <Typography variant="subtitle1">Files in Change</Typography>
                    </Box>
                    <Divider />
                    <List dense>
                      {changeFiles.map((f) => (
                        <ListItemButton key={f.id || f.file_id}>
                          <ListItemText
                            primary={f.path || f.file_path}
                            secondary={
                              <>
                                {f.module && (
                                  <span>
                                    Module: <strong>{f.module}</strong>
                                  </span>
                                )}
                                {f.description && <div>{f.description}</div>}
                              </>
                            }
                          />
                        </ListItemButton>
                      ))}
                      {changeFiles.length === 0 && (
                        <Typography p={2} color="text.secondary">
                          No files associated with this change.
                        </Typography>
                      )}
                    </List>
                  </Box>

                  {/* History */}
                  <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <Box p={2} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                      <Typography variant="subtitle1">Change History</Typography>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="change-history-filter-label">Filter</InputLabel>
                        <Select
                          labelId="change-history-filter-label"
                          value={historyFilter}
                          label="Filter"
                          onChange={(e) => setHistoryFilter(e.target.value)}
                        >
                          <MenuItem value="ALL">All</MenuItem>
                          <MenuItem value="CODE">Code</MenuItem>
                          <MenuItem value="DOCUMENTATION">Documentation</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Divider />
                    <List dense>
                      {filteredChangeHistory.map((h) => (
                        <ListItemButton
                          key={h.id}
                          sx={{ alignItems: "flex-start" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              flex: 1,
                            }}
                          >
                            <ListItemText
                              primary={h.save_summary || "(no summary)"}
                              secondary={
                                <>
                                  {h.file_path ? <div>{h.file_path}</div> : null}
                                  <div>{h.saved_at}</div>
                                  <div>{isDocumentationHistoryEntry(h) ? "Type: Documentation" : "Type: Code"}</div>
                                </>
                              }
                            />
                            {(h.preview_log_id || h.backup_path) && (
                              <Box sx={{ mt: 0.5 }}>
                                {h.preview_log_id && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreviewDiff(h);
                                    }}
                                  >
                                    Preview diff
                                  </Button>
                                )}
                                {h.backup_path && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRevertHistoryEntry(h);
                                    }}
                                    sx={{ ml: h.preview_log_id ? 1 : 0 }}
                                  >
                                    Revert
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        </ListItemButton>
                      ))}
                      {filteredChangeHistory.length === 0 && (
                        <Typography p={2} color="text.secondary">
                          No history entries for this change.
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit change description</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={12}
            maxRows={20}
            margin="dense"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            inputProps={{ "aria-label": "Edit change description" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescriptionModalOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <ChangesPreviewDiffDialog
        open={previewOpen}
        onClose={handleClosePreview}
        loading={previewLoading}
        error={previewError}
        data={previewData}
      />
    </>
  );
};

export default ChangesPanel;
